import { FormMessages } from "@components/form-messages";
import { Button } from "@components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandList,
} from "@components/ui/command";
import { useDebounce } from "@hooks/use-debounce";
import { Delete, Loader2, Pencil } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { env } from "src/env.mjs";
import AddressDialog from "./address-dialog";

import CustomInputField, {
  CustomInputFieldProps,
} from "@components/FormFields/CustomInputField";
import { useDataQuery } from "@hooks/useData/useDataQuery";
import { Command as CommandPrimitive } from "cmdk";
import { MdLocationOn } from "react-icons/md";
import { z } from "zod";

const getAddressAutocomplete = (async (input?: string) => {
  const apiKey = env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY;

  if (!input?.length) return [];
  const url = "https://places.googleapis.com/v1/places:autocomplete";

  const primaryTypes = [
    "street_address",
    "subpremise",
    "route",
    "street_number",
    "landmark",
  ];

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": apiKey,
        Referer: "http://app.signagestudio.io/",
      },
      body: JSON.stringify({
        input: input,
        includedPrimaryTypes: primaryTypes,
        // Location biased towards the user's country
        // includedRegionCodes: [country || "US"],
      }),
    });

    if (!response.ok) {
      return [];
      // throw new Error("Something went wrong");
    }

    const data = await response.json();

    return data.suggestions;
  } catch (error) {
    throw new Error("Something went wrong");
  }
});

const getAddressPlace = (async ({ placeId }: { placeId: string }) => {
  if (!placeId?.length) return;
  const apiKey = env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY;

  const url = `https://places.googleapis.com/v1/${placeId}`;

  try {
    const response = await fetch(url, {
      headers: {
        "X-Goog-Api-Key": apiKey,
        "X-Goog-FieldMask":
          // Include expected fields in the response
          "adrFormatAddress,shortFormattedAddress,formattedAddress,location,addressComponents",
        "Content-Type": "application/json",
        Referer: "http://app.signagestudio.io/",
      },
    });

    if (!response.ok) {
      throw new Error("Something went wrong");
    }

    const data = await response.json();

    const dataFinderRegx = (c: string) => {
      const regx = new RegExp(`<span class="${c}">([^<]+)<\/span>`);
      const match = data.adrFormatAddress.match(regx);
      return match ? match[1] : "";
    };

    const address1 = dataFinderRegx("street-address");
    const address2 = "";
    const city = dataFinderRegx("locality");
    const region = dataFinderRegx("region");
    const postalCode = dataFinderRegx("postal-code");
    const country = dataFinderRegx("country-name");
    const lat = data.location.latitude;
    const lng = data.location.longitude;

    const formattedAddress = data.formattedAddress;

    const formattedData: AddressType = {
      address1,
      address2,
      formattedAddress,
      city,
      region,
      postalCode,
      country,
      lat,
      lng,
    };
    return {
      address: formattedData,
      adrAddress: data.adrFormatAddress,
    };
  } catch (err) {
    throw new Error("Something went wrong");
  }
});

export const addressSchema = z.object({
  address1: z.string({ message: "Address line 1 is required" }),
  address2: z.string().optional(),
  formattedAddress: z.string().optional(),
  city: z.string({ message: "City is required" }),
  region: z.string({ message: "State is required" }).default("NA"),
  postalCode: z.string().optional(),
  country: z.string(),
  lat: z.coerce.number(),
  lng: z.coerce.number(),
});

export type AddressType = z.infer<typeof addressSchema>;

interface AddressAutoCompleteProps
  extends CustomInputFieldProps<HTMLInputElement> {
  address: AddressType;
  setAddress: (address: AddressType) => void;
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
  dialogTitle: string;
  showInlineError?: boolean;
  placeholder?: string;
}

const AddressAutoComplete = React.forwardRef<
  HTMLInputElement,
  AddressAutoCompleteProps
>(
  (
    {
      address,
      setAddress,
      dialogTitle,
      showInlineError = true,
      searchInput,
      setSearchInput,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const [selectedPlaceId, setSelectedPlaceId] = useState("");
    const [isOpen, setIsOpen] = useState(false);

    const { data, isLoading } = useDataQuery({
      queryKey: ["google-address", selectedPlaceId],
      queryFn: () => getAddressPlace({ placeId: selectedPlaceId }),
      options: {
        enabled: !!selectedPlaceId?.length,
        refetchOnWindowFocus: false,
      }
    });

    const adrAddress = data?.adrAddress;

    useEffect(() => {
      if (data?.address) {
        setAddress(data.address);
      }
    }, [data, setAddress]);

    return (
      <>
        {selectedPlaceId !== "" || address.formattedAddress ? (
          <div className="flex items-center gap-2">
            <CustomInputField
              ref={ref}
              value={address?.formattedAddress ?? ""}
              readOnly
              {...props}
            />

            <AddressDialog
              isLoading={isLoading}
              dialogTitle={dialogTitle}
              adrAddress={adrAddress || address?.formattedAddress || ""}
              address={address}
              setAddress={setAddress}
              open={isOpen}
              setOpen={setIsOpen}
            >
              <Button
                disabled={isLoading}
                size="icon"
                variant="outline"
                className="shrink-0"
              >
                <Pencil className="size-4" />
              </Button>
            </AddressDialog>
            <Button
              type="reset"
              onClick={() => {
                setSelectedPlaceId("");
                setAddress({
                  address1: "",
                  address2: "",
                  formattedAddress: "",
                  city: "",
                  region: "",
                  postalCode: "",
                  country: "",
                  lat: 0,
                  lng: 0,
                });
              }}
              size="icon"
              variant="outline"
              className="shrink-0"
            >
              <Delete className="size-4" />
            </Button>
          </div>
        ) : (
          <AddressAutoCompleteInput
            ref={ref}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            selectedPlaceId={selectedPlaceId}
            setSelectedPlaceId={setSelectedPlaceId}
            setIsOpenDialog={setIsOpen}
            showInlineError={showInlineError}
            placeholder={placeholder}
          />
        )}
      </>
    );
  },
);
export default AddressAutoComplete;

interface CommonProps {
  selectedPlaceId: string;
  setSelectedPlaceId: (placeId: string) => void;
  setIsOpenDialog: (isOpen: boolean) => void;
  showInlineError?: boolean;
  searchInput: string;
  setSearchInput: (searchInput: string) => void;
  placeholder?: string;
}

const AddressAutoCompleteInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  CommonProps
>((props, ref) => {
  const {
    setSelectedPlaceId,
    selectedPlaceId,
    setIsOpenDialog,
    showInlineError,
    searchInput,
    setSearchInput,
    placeholder,
  } = props;

  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      close();
    }
  };

  const debouncedSearchInput = useDebounce(searchInput, 500);

  const { data: predictions = [], isLoading } = useDataQuery({
    queryKey: ["google-autocomplete", debouncedSearchInput],
    queryFn: () => getAddressAutocomplete(debouncedSearchInput),
  });

  return (
    <Command
      shouldFilter={false}
      onKeyDown={handleKeyDown}
      className="overflow-visible"
    >
      <CustomInputField
        leadingIcon={<MdLocationOn />}
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        onBlur={close}
        onFocus={open}
        autoComplete="off"
        placeholder={placeholder || "Enter address"}
      />
      {searchInput !== "" && !isOpen && !selectedPlaceId && showInlineError && (
        <FormMessages
          type="error"
          className="pt-1 text-sm"
          messages={["Select a valid address from the list"]}
        />
      )}

      {isOpen && (
        <div className="relative animate-in fade-in-0 zoom-in-95 h-auto">
          <CommandList>
            <div className="absolute top-1.5 z-50 w-full">
              <CommandGroup className="relative h-auto z-50 min-w-[8rem] overflow-hidden rounded-md border shadow-md bg-background">
                {isLoading ? (
                  <div className="h-28 flex items-center justify-center">
                    <Loader2 className="size-6 animate-spin" />
                  </div>
                ) : (
                  <>
                    {predictions.map(
                      (prediction: {
                        placePrediction: {
                          placeId: string;
                          place: string;
                          text: { text: string };
                        };
                      }) => (
                        <CommandPrimitive.Item
                          value={prediction.placePrediction.text.text}
                          onSelect={() => {
                            setSearchInput("");
                            setSelectedPlaceId(
                              prediction.placePrediction.place,
                            );
                            setIsOpenDialog(true);
                          }}
                          className="flex select-text flex-col cursor-pointer gap-0.5 h-max p-2 px-3 rounded-md aria-selected:bg-accent aria-selected:text-accent-foreground hover:bg-accent hover:text-accent-foreground items-start"
                          key={prediction.placePrediction.placeId}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {prediction.placePrediction.text.text}
                        </CommandPrimitive.Item>
                      ),
                    )}
                  </>
                )}

                <CommandEmpty>
                  {!isLoading && predictions.length === 0 && (
                    <div className="py-4 flex items-center justify-center">
                      {searchInput === ""
                        ? "Please enter an address"
                        : "No address found"}
                    </div>
                  )}
                </CommandEmpty>
              </CommandGroup>
            </div>
          </CommandList>
        </div>
      )}
    </Command>
  );
});
